import React from 'react';
import { ClipsPageInfo, ClipSuggestion } from 'blocks/ClipSelect/types';
import { EddyClipSuggestion } from 'blocks/EddyEmbed';
import { AudioClipperComponent } from 'components/AudioClipper';
import { AsyncAudioClipperProps } from 'containers/AsyncAudioClipper';
import { ClipSuggestionDislikeReason, IEmbedConfig } from 'types';
import { HighlightedRegionState } from './useDefaultHighlightedRegion';

export type VideoPlayerRefs = Record<string, React.RefObject<HTMLVideoElement>>;

export type Region = Required<AsyncAudioClipperProps['region']> & {
  /**
   * a suggestionId should exist if this region corresponds to a suggestion
   */
  suggestionId?: number;
};

export enum ClipSelectionType {
  DEFAULT = 'Default',
  SUGGESTED_CLIP = 'SuggestedClip',
  CUSTOM_CLIP = 'CustomClip',
}

export interface FormValues {
  region: Region;
  lastClipSelectionType: ClipSelectionType;
  isCaptionEnabled: boolean;
  clipSuggestion: ClipSuggestion;
}

export type OnSelectClip = (
  clip: Pick<FormValues, 'region'> & {
    activeSuggestionId?: number;
  },
) => Promise<number | void>;

export type ReprocessClipContext = 'preview' | 'export';

type OnReprocessClipOptions = {
  configJson?: IEmbedConfig;
  context?: ReprocessClipContext;
};

export type OnReprocessClip = (
  clipId: number,
  options?: OnReprocessClipOptions,
) => Promise<void>;

/**
 * Prepares the given suggestion for the Advanced Editor and resolves to the
 * editor URL to open in a new tab, or `undefined` when there is nothing to
 * open — i.e. it failed, in which case the error is surfaced to the user.
 *
 * The result is handed to a `useRunInDeferredTab` caller, which opens the
 * resolved URL in a popup-blocker-safe tab, or closes the placeholder tab when
 * it resolves to `undefined`.
 */
export type OnOpenInAdvancedEditor = (
  clipSuggestion: ClipSuggestion,
) => Promise<string | undefined>;

export interface VideoPlayerControls {
  playing: boolean;
  onPlay: () => void;
  onPause: () => void;
}

export type UpdatedSuggestionProjectNames = Record<string, string>;

export type ClipSelectMediaType = 'audio' | 'video';

export type OnSubmitOptions = {
  skipStaleCheck?: boolean;
};

type SuggestionCallback<T = void> = (suggestion: ClipSuggestion) => Promise<T>;

export type ClipSelectContextType = {
  requestId?: number;
  exportingSuggestionIds: Set<number>;
  activeSuggestionId: number;
  defaultClipperRegion: HighlightedRegionState['clipper'];
  defaultTranscriptRegion: HighlightedRegionState['transcript'];
  dislikedSuggestionIds: number[];
  eddyProjectId?: string;
  audioClipperRef: React.RefObject<AudioClipperComponent>;
  isSuggestionPlaying: boolean;
  region?: Region;
  removeSuggestion: (id: number) => void;
  updateSuggestionProjectName: (clipId: number, projectName: string) => void;
  isClipSuggestionLoading: boolean;
  suggestionIds: number[] | undefined;
  onSuggestionSelect: (suggestionId: number | null) => void;
  onAdjust: () => void;
  onEditTemplate: (suggestionId: number) => void;
  onCustom?: () => void;
  /**
   * Resolves to the share URL to open in a new tab via `useRunInDeferredTab`,
   * or `undefined` if there is nothing to open. See {@link OnSubmit}.
   */
  prepareShareUrl: (suggestionId: number) => Promise<string | undefined>;
  onSelectClip: OnSelectClip;
  onReprocessClip: OnReprocessClip;
  /**
   * Resolves to the Advanced Editor URL to open in a new tab via
   * `useRunInDeferredTab`, or `undefined` if there is nothing to open.
   * See {@link OnOpenInAdvancedEditor}.
   */
  prepareAdvancedEditorUrl: (
    suggestionId: number,
  ) => Promise<string | undefined>;
  visibleSuggestionIds: number[];
  updatedSuggestionProjectNames: UpdatedSuggestionProjectNames;
  activeSuggestion: ClipSuggestion;
  hasClipSuggestions: boolean;
  clipSelectionType?: ClipSelectionType;
  suggestedClips: ClipSuggestion[];
  clipsPageInfo: ClipsPageInfo;
  videoPlayerControls: VideoPlayerControls;
  mediaType: ClipSelectMediaType;
  eddySuggestedClips: EddyClipSuggestion[];
  /**
   * Whether ANY of the suggestions are generating a preview.
   */
  isGeneratingPreviews: boolean;
  onTranscriptUpdate: () => void;
  onDislikeClip?: (
    reason: ClipSuggestionDislikeReason,
    clipSuggestion: ClipSuggestion,
    onClipDisliked?: () => void,
  ) => void;
  onUpdateSuggestions?: () => Promise<ClipSuggestion[]>;
  onUpdateSuggestion?: (suggestionId: number) => Promise<ClipSuggestion>;
  pauseAll?: () => void;
  exportClip: (suggestionId: number, cb?: SuggestionCallback) => Promise<void>;
  onDownloadAll: () => void;
  onExportAll: () => void;
};

export interface BoundClipSelectContextType
  extends Omit<
    ClipSelectContextType,
    | 'exportClip'
    | 'onReprocessClip'
    | 'onUpdateSuggestion'
    | 'updateSuggestionProjectName'
  > {
  exportClip: (cb?: SuggestionCallback) => Promise<void>;
  onEditTemplate: () => void;
  prepareAdvancedEditorUrl: () => Promise<string | undefined>;
  prepareShareUrl: () => Promise<string | undefined>;
  onSuggestionSelect: (suggestionId?: null) => void;
  onUpdateSuggestion: () => Promise<ClipSuggestion>;
  onReprocessClip: (opts?: OnReprocessClipOptions) => Promise<void>;
  removeSuggestion: () => void;
  updateSuggestionProjectName: (projectName: string) => void;
  suggestion: ClipSuggestion;
  updatedSuggestionProjectName: string | undefined;
  isExporting: boolean;
  /**
   * Whether the CURRENT suggestion is generating a preview.
   */
  isGeneratingPreview: boolean;
}

/**
 * Shares the given suggestion and resolves to the URL to open in a new tab
 * (the share destination), or `undefined` when there is nothing to open — e.g.
 * the share failed, in which case the error is surfaced to the user.
 *
 * The result is handed to a `useRunInDeferredTab` caller, which opens the
 * resolved URL in a popup-blocker-safe tab, or closes the placeholder tab when
 * it resolves to `undefined`.
 */
export type OnSubmit = (
  suggestion: ClipSuggestion,
) => Promise<string | undefined>;

export type ClipSelectProviderProps = React.PropsWithChildren<
  Pick<
    ClipSelectContextType,
    | 'requestId'
    | 'suggestedClips'
    | 'dislikedSuggestionIds'
    | 'clipsPageInfo'
    | 'mediaType'
    | 'onDislikeClip'
    | 'onReprocessClip'
    | 'onUpdateSuggestions'
    | 'onUpdateSuggestion'
    | 'onSelectClip'
    | 'onDownloadAll'
    | 'onExportAll'
  > & {
    eddyProjectId?: string;
    onOpenInAdvancedEditor: OnOpenInAdvancedEditor;
    onSubmit: OnSubmit;
  }
>;

export type UseClipSelectProps = {
  clipId: number;
};
